'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw, Home, HelpCircle, Search, Settings, TrendingUp } from 'lucide-react';
import Link from 'next/link';

import Footer from '@/components/Footer';
import ErrorFeedback from './ErrorFeedback';
import { safeUrl } from '@/utils/safe-url';
import { capturePostHogException } from '@/analytics/posthog';
import { reloadOnceForChunkError } from '@/utils/chunkReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  user?: Record<string, unknown>;
  loading?: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, loading: true };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: undefined };
  }

  componentDidMount() {
    this.checkUserStatus();
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    // Render crashes caught here never reached PostHog error tracking, so a
    // user stuck on this screen left no stack trace (replay vision flagged a
    // blocked /subscription on 2026-10-02 with no matching $exception).
    // Same reporter the route-level error.tsx files already use.
    // Reporting must never break the fallback UI.
    try {
      Promise.resolve(capturePostHogException(error)).catch(() => {});
    } catch {
      // ignore
    }
    this.sendErrorNotification(error, errorInfo);
    // A missing chunk is the previous build's hash, not a calculation error.
    // Reload once onto the build the server is serving now. A non-chunk error
    // is ignored, so this screen still shows for everything else.
    reloadOnceForChunkError(error);
  }

  private checkUserStatus = () => {
    try {
      const userCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('user='))
        ?.split('=')[1];
      
      if (userCookie) {
        const userData = JSON.parse(decodeURIComponent(userCookie));
        this.setState({ user: userData });
      }
    } catch (_error) {
      console.log('No user cookie found');
    } finally {
      this.setState({ loading: false });
    }
  };

  private async sendErrorNotification(error: Error, errorInfo: ErrorInfo) {
    try {
      // Get user info from cookie if available
      let userId: string | undefined;
      let email: string | undefined;
      try {
        const userCookie = document.cookie
          .split('; ')
          .find(row => row.startsWith('user='))
          ?.split('=')[1];
        
        if (userCookie) {
          const userData = JSON.parse(decodeURIComponent(userCookie));
          userId = userData.id;
          email = userData.email;
        }
      } catch (_e) {
        // No user cookie
      }

      // Get portfolio and scenario context from localStorage if available
      let portfolioId: string | undefined;
      let portfolioName: string | undefined;
      let scenarioId: string | undefined;
      let scenarioName: string | undefined;
      
      try {
        const portfolioCookie = document.cookie
          .split('; ')
          .find(row => row.startsWith('portfolio='))
          ?.split('=')[1];
        
        if (portfolioCookie) {
          const portfolioData = JSON.parse(decodeURIComponent(portfolioCookie));
          portfolioId = portfolioData.id;
          portfolioName = portfolioData.name;
        }
      } catch (_e) {
        // No portfolio cookie
      }
      
      try {
        const scenarioCookie = document.cookie
          .split('; ')
          .find(row => row.startsWith('scenario='))
          ?.split('=')[1];
        
        if (scenarioCookie) {
          const scenarioData = JSON.parse(decodeURIComponent(scenarioCookie));
          scenarioId = scenarioData.id;
          scenarioName = scenarioData.name;
        }
      } catch (_e) {
        // No scenario cookie
      }

      const errorData = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: safeUrl(window.location.href),
        userId,
        email,
        portfolioId,
        portfolioName,
        scenarioId,
        scenarioName,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        platform: navigator.platform,
        browser: this.detectBrowser()
      };

      await fetch('/api/client-error', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData)
      });
    } catch (notificationError) {
      console.error('Failed to send error notification:', notificationError);
    }
  }

  private detectBrowser() {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  private getPrimaryAction = () => {
    const { loading, user } = this.state;
    if (loading) {
      return {
        text: 'View Home',
        href: '/'
      };
    }
    if (user) {
      return {
        text: 'Return to Dashboard',
        href: '/dashboard'
      };
    } else {
      return {
        text: 'View Home',
        href: '/'
      };
    }
  };

  render() {
    if (this.state.hasError) {
      const primaryAction = this.getPrimaryAction();
      
      return this.props.fallback || (
        <div className="min-h-screen bg-gray-50 flex flex-col">
          <div className="flex-grow">
            {/* Hero Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
              <div className="text-center">
                <div className="text-[8rem] font-bold text-gray-900 mb-8 leading-none">
                  500
                </div>
                
                <div className="max-w-3xl mx-auto">
                  <div className="bg-white p-8 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                    <h1 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                      This calculation needs adjusting
                    </h1>
                    
                    <div className="text-lg text-gray-600 font-mono text-right mb-8">
                      System recalculating.
                    </div>
                    
                    <div className="space-y-4 text-gray-700 mb-8">
                      <p className="text-lg leading-relaxed">
                        We're sorry, but something unexpected happened with our financial calculations. 
                        This sometimes occurs during complex portfolio analysis or market data processing.
                      </p>
                      
                      <p className="text-lg leading-relaxed">
                        Think of it like market volatility - temporary disruptions that we resolve quickly to get you back on track.
                      </p>
                    </div>

                    {/* Primary Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center">
                      <button
                        onClick={() => window.location.reload()}
                        className="bg-positive-600 hover:bg-positive-700 text-white font-bold py-3 px-8 rounded-md border-2 border-ink shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all hover:shadow-none text-center flex items-center justify-center"
                      >
                        Refresh Page
                        <RefreshCw className="ml-2 w-5 h-5" />
                      </button>
                      
                      <Link
                        href={primaryAction.href}
                        className="bg-white hover:bg-gray-100 text-gray-800 font-bold py-3 px-8 rounded-md border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all hover:shadow-none flex items-center justify-center"
                      >
                        <Home className="mr-2 w-5 h-5" />
                        {primaryAction.text}
                      </Link>
                    </div>
                    
                    {/* Whimsy Element - subtle dry humor for error recovery */}
                    <div className="text-center">
                      <p className="text-xs text-gray-500 font-mono">
                        Temporary setback. Financial planning resumes.
                      </p>
                    </div>
                    
                    {/* Development Error Details */}
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                      <details className="mt-8 border-2 border-black rounded-lg p-4 bg-gray-50">
                        <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-3 hover:text-gray-900">
                          Error Details (Development Only)
                        </summary>
                        <div className="text-xs">
                          <pre className="whitespace-pre-wrap word-break break-word text-gray-600 font-mono bg-white p-3 rounded border border-gray-200 overflow-auto max-h-48">
                            {this.state.error.stack}
                          </pre>
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Action Guidance Section */}
            <section className="py-16 bg-white border-t-2 border-b-2 border-black">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-center mb-12">Next steps to get back on track</h2>
                
                <div className="grid md:grid-cols-3 gap-6 mb-12">
                  <div className="p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-positive-50">
                    <div className="w-12 h-12 flex items-center justify-center bg-positive-100 rounded-full mb-4">
                      <RefreshCw className="w-6 h-6 text-positive-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">1. Refresh the Page</h3>
                    <p className="text-gray-700">
                      Most errors resolve themselves with a simple refresh
                    </p>
                  </div>
                  
                  <div className="p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-authority-50">
                    <div className="w-12 h-12 flex items-center justify-center bg-authority-100 rounded-full mb-4">
                      <Home className="w-6 h-6 text-authority-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">2. Return to Dashboard</h3>
                    <p className="text-gray-700">
                      Access your portfolio from the main dashboard
                    </p>
                  </div>
                  
                  <div className="p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-danger-50">
                    <div className="w-12 h-12 flex items-center justify-center bg-danger-100 rounded-full mb-4">
                      <HelpCircle className="w-6 h-6 text-danger-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">3. Get Support</h3>
                    <p className="text-gray-700">
                      Contact our team if the problem persists
                    </p>
                  </div>
                </div>

                {/* User Feedback Form */}
                {this.state.error && (
                  <div className="max-w-2xl mx-auto mt-12">
                    <ErrorFeedback 
                      error={this.state.error} 
                      componentStack={(this.state as State & { errorInfo?: { componentStack?: string } }).errorInfo?.componentStack}
                      url={typeof window !== 'undefined' ? window.location.href : ''}
                    />
                  </div>
                )}
              </div>
            </section>

            {/* Helpful Links Section */}
            <section className="py-16 bg-gray-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-center mb-12">Looking for something specific?</h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {!this.state.loading && this.state.user ? (
                    <>
                      <Link href="/dashboard" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                        <div className="w-12 h-12 flex items-center justify-center bg-positive-100 rounded-full mb-4 group-hover:bg-positive-200 transition-colors">
                          <Home className="w-6 h-6 text-positive-600" />
                        </div>
                        <h3 className="text-lg font-bold mb-2 group-hover:text-positive-600 transition-colors">Dashboard</h3>
                        <p className="text-gray-600 text-sm">View your portfolio overview</p>
                      </Link>
                      
                      <Link href="/transactions" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                        <div className="w-12 h-12 flex items-center justify-center bg-authority-100 rounded-full mb-4 group-hover:bg-authority-200 transition-colors">
                          <Search className="w-6 h-6 text-authority-600" />
                        </div>
                        <h3 className="text-lg font-bold mb-2 group-hover:text-authority-600 transition-colors">Transactions</h3>
                        <p className="text-gray-600 text-sm">Manage your transactions</p>
                      </Link>
                      
                      <Link href="/assets" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                        <div className="w-12 h-12 flex items-center justify-center bg-positive-100 rounded-full mb-4 group-hover:bg-positive-200 transition-colors">
                          <TrendingUp className="w-6 h-6 text-positive-600" />
                        </div>
                        <h3 className="text-lg font-bold mb-2 group-hover:text-positive-600 transition-colors">Assets</h3>
                        <p className="text-gray-600 text-sm">Track your assets</p>
                      </Link>
                      
                      <Link href="/settings" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                        <div className="w-12 h-12 flex items-center justify-center bg-accessible-assets-100 rounded-full mb-4 group-hover:bg-accessible-assets-200 transition-colors">
                          <Settings className="w-6 h-6 text-accessible-assets-600" />
                        </div>
                        <h3 className="text-lg font-bold mb-2 group-hover:text-accessible-assets-600 transition-colors">Settings</h3>
                        <p className="text-gray-600 text-sm">Manage your account</p>
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link href="/pricing" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                        <div className="w-12 h-12 flex items-center justify-center bg-positive-100 rounded-full mb-4 group-hover:bg-positive-200 transition-colors">
                          <TrendingUp className="w-6 h-6 text-positive-600" />
                        </div>
                        <h3 className="text-lg font-bold mb-2 group-hover:text-positive-600 transition-colors">Pricing</h3>
                        <p className="text-gray-600 text-sm">See our plans</p>
                      </Link>
                      
                      <Link href="/features" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                        <div className="w-12 h-12 flex items-center justify-center bg-authority-100 rounded-full mb-4 group-hover:bg-authority-200 transition-colors">
                          <Search className="w-6 h-6 text-authority-600" />
                        </div>
                        <h3 className="text-lg font-bold mb-2 group-hover:text-authority-600 transition-colors">Solutions</h3>
                        <p className="text-gray-600 text-sm">Explore what we offer</p>
                      </Link>
                      
                      <Link href="/guides" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                        <div className="w-12 h-12 flex items-center justify-center bg-positive-100 rounded-full mb-4 group-hover:bg-positive-200 transition-colors">
                          <HelpCircle className="w-6 h-6 text-positive-600" />
                        </div>
                        <h3 className="text-lg font-bold mb-2 group-hover:text-positive-600 transition-colors">Guides</h3>
                        <p className="text-gray-600 text-sm">Learn about finance</p>
                      </Link>
                      
                      <Link href="/help" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                        <div className="w-12 h-12 flex items-center justify-center bg-accessible-assets-100 rounded-full mb-4 group-hover:bg-accessible-assets-200 transition-colors">
                          <HelpCircle className="w-6 h-6 text-accessible-assets-600" />
                        </div>
                        <h3 className="text-lg font-bold mb-2 group-hover:text-accessible-assets-600 transition-colors">Help</h3>
                        <p className="text-gray-600 text-sm">Get support</p>
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </section>
          </div>
          <Footer />
          
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
